import React, { ReactElement } from 'react';
import { Navigate, RouteObject, useRoutes } from 'react-router-dom';

import {
  LOGIN_PAGE,
  ADMIN_PAGE,
  WOHNEN_PLUS_PATH,
  MIETER_PLUS_PATH,
  KAEUFER_PLUS_PATH,
  WELCOME_MODAL,
  FINO_PATH,
  CREDIT_HISTORIE,
} from '../domain/routes';
import { IssueType } from '../@types/issueType';
import { getConfig } from '../config';
import { AdminPageContainer } from './common/admin/AdminPageContainer';
import { LocalLoginPageManager } from './content/localLoginPage/LocalLoginPageManager';
import { BenefitsPageManager } from './content/BenefitsPageManager';
import FinoPage from './content/FinoPage';
import { IssuePage } from './content/issuePage/IssuePage';
import { MODALS } from './config/modals';
import { CreditHistoryPage } from './content/creditHistoryPage/CreditHistoryPage';

/**
 * Paths associated with the Wohnen Plus benefits.
 */
const WOHNEN_PATHS = (MODALS?.WOHNENPLUS && Object.keys(MODALS.WOHNENPLUS)) || [];

/**
 * Paths associated with the Mieter Plus benefits.
 */
const MIETER_PATHS = (MODALS?.MIETERPLUS && Object.keys(MODALS.MIETERPLUS)) || [];
/**
 * Paths associated with the Kaeufer Plus benefits.
 */
const KAEUFER_PATHS = (MODALS?.KAEUFERPLUS && Object.keys(MODALS.KAEUFERPLUS)) || [];

/**
 * Generates redirect routes for legacy paths. example: /meinplus/vorteile/nebenkosten-check to /meinplus/vorteile/wohnenplus/nebenkosten-check
 * @param paths Array of string paths for which to generate redirect routes.
 * @param basePath The base path to prepend to each path for redirection.
 * @returns An array of RouteObject configured for redirection.
 */
const generateLegacyRoutes = (paths: string[], basePath: string): RouteObject[] =>
  paths.map((path) => ({
    path,
    element: <Navigate to={`/${basePath}/${path}`} replace />,
  }));

/**
 * Generates child routes for a given set of paths.
 * Each child route renders the BenefitsPageManager component.
 * @param paths Array of string paths for which to generate child routes.
 * @returns An array of RouteObject for child routes.
 */
const generateChildRoutes = (paths: string[]) =>
  paths
    .map((path) => ({
      path: `${path}/*`,
      element: <BenefitsPageManager />,
    }))
    .concat([
      {
        index: true,
        element: <BenefitsPageManager />,
      } as any,
    ]);

/**
 * The main router component for the application.
 * Configures and returns routes based on the application configuration.
 * @returns A ReactElement or null representing the configured routes.
 */
export const AppRouter: React.FC = (): ReactElement | null => {
  const { showAdminPage, publicUrl } = getConfig();

  const baseRoutes: RouteObject[] = [
    { index: true, element: <Navigate to={MIETER_PLUS_PATH} replace /> },
    { index: true, element: <BenefitsPageManager /> },
    { path: FINO_PATH, element: <FinoPage /> },
    { path: CREDIT_HISTORIE, element: <CreditHistoryPage /> },
    ...generateLegacyRoutes(WOHNEN_PATHS, WOHNEN_PLUS_PATH),
    ...generateLegacyRoutes(MIETER_PATHS, MIETER_PLUS_PATH),
    {
      path: MIETER_PLUS_PATH,
      children: generateChildRoutes(MIETER_PATHS.concat([WELCOME_MODAL])),
    },
    {
      path: WOHNEN_PLUS_PATH,
      children: generateChildRoutes(WOHNEN_PATHS.concat([WELCOME_MODAL])),
    },
    {
      path: KAEUFER_PLUS_PATH,
      children: generateChildRoutes(KAEUFER_PATHS.concat([])),
    },
  ];

  // If the showAdminPage flag is set to 'true', add the AdminPage route to the baseRoutes array
  if (showAdminPage === 'true') {
    baseRoutes.push(
      { path: ADMIN_PAGE, element: <AdminPageContainer /> },
      { path: LOGIN_PAGE, element: <LocalLoginPageManager /> }, // TODO: ASK: should this path be in PROD - TEAM OFFLINE ?
    );
  }

  const routes = useRoutes([
    {
      path: publicUrl,
      children: baseRoutes,
    },
    { path: '*', element: <IssuePage type={IssueType.NOT_FOUND} /> },
  ]);

  return routes;
};
